import { createElement } from 'react';
import { Home, Sprout, ListChecks, Stethoscope, Gamepad2, Warehouse, Wheat,
  LayoutGrid, Sparkles, CalendarDays, Users, UserRound, Crown, HelpCircle,
  ShieldCheck, BookOpen, TreePine, MapPinned, Store, Trophy, Award, Camera } from 'lucide-react';
import SpeciesArt from '../components/species/SpeciesArt';

// Reuse the existing catalogue vector: no photo fetch, animation or new asset.
const mushroomLook = Object.freeze({ k: 'cap', cap: '#efe4ce', stem: '#efe4ce', capW: 72, capH: 30, stemW: 18 });
export function MushroomNavigationIcon({ className = '' }) {
  return createElement(SpeciesArt, { look: mushroomLook, seed: 'navigation-mushroom', className });
}

const appearance = (id, icon, light, dark) => Object.freeze({ id, icon, accent: id,
  light, dark, active: light });

// A function has one icon and colour across the dock, More and guide entries.
// Labels use the app's neutral text tokens; colour is an extra recognition cue.
export const NAVIGATION_IDENTITIES = Object.freeze({
  '/dashboard': appearance('home', Home, '#17646a', '#89cfd3'),
  '/my-garden': appearance('garden', Sprout, '#386241', '#aed19b'),
  '/tasks': appearance('tasks', ListChecks, '#90600d', '#edc885'),
  '/garden-doctor': appearance('doctor', Stethoscope, '#216f91', '#90cce5'),
  '/eden': appearance('eden', Gamepad2, '#876219', '#f2d196'),
  '/shed': appearance('shed', Warehouse, '#8e5236', '#deb398'),
  '/harvest': appearance('harvest', Wheat, '#796314', '#e7d095'),
  '/bed-layout': appearance('beds', LayoutGrid, '#955146', '#e9ae9b'),
  '/weekly-plan': appearance('weekly-plan', Sparkles, '#695393', '#cdb8ed'),
  '/calendar': appearance('calendar', CalendarDays, '#236e76', '#98d2d4'),
  '/community': appearance('community', Users, '#944961', '#e5afc1'),
  '/profile': appearance('profile', UserRound, '#626359', '#c7c8b9'),
  '/premium': appearance('premium', Crown, '#876219', '#f2d196'),
  '/help': appearance('help', HelpCircle, '#386b80', '#aed3e4'),
  '/privacy': appearance('privacy', ShieldCheck, '#586576', '#b9c8dd'),
  '/discover': appearance('discover', BookOpen, '#446b3d', '#b6d3a9'),
  '/foraging': appearance('foraging', TreePine, '#326c56', '#98c9b1'),
  '/finds-map': appearance('map', MapPinned, '#315c9d', '#aec8f4'),
  '/mushrooms': appearance('mushrooms', MushroomNavigationIcon, '#795795', '#d4b4e6'),
  '/id': appearance('camera', Camera, '#95445e', '#efa7bf'),
  '/marketplace': appearance('marketplace', Store, '#826128', '#e9cd9b'),
  '/leaderboard': appearance('leaderboard', Trophy, '#876219', '#f2d196'),
  '/achievements': appearance('achievements', Award, '#695393', '#cdb8ed'),
  '/more': appearance('more', LayoutGrid, '#686048', '#d7caaa'),
});

const ALIASES = { '/min-have':'/my-garden', '/opgaver':'/tasks', '/havedoktor':'/garden-doctor',
  '/opdag':'/discover', '/kalender':'/calendar', '/profil':'/profile', '/uge-plan':'/weekly-plan',
  '/svampe':'/mushrooms', '/svampe-id':'/mushroom-identify', '/vild-guide':'/foraging',
  '/privatliv':'/privacy', '/skuret':'/shed' };

export function canonicalNavigationPath(path = '') {
  const bare = String(path).split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  if (/^\/[a-z]{2}\/discover(?:\/|$)/i.test(bare)) return bare.replace(/^\/[a-z]{2}/i, '');
  const alias = Object.keys(ALIASES).find(route => bare === route || bare.startsWith(`${route}/`));
  return alias ? `${ALIASES[alias]}${bare.slice(alias.length)}` : bare;
}

export function navigationOnPath(pathname, to) {
  const path = canonicalNavigationPath(pathname), destination = canonicalNavigationPath(to);
  // Mushroom identification belongs to its guide, while Instant ID remains a
  // separate camera destination. No route itself is rewritten or redirected.
  return path === destination || path.startsWith(`${destination}/`)
    || (destination === '/mushrooms' && path === '/mushroom-identify');
}

export function navigationIdentity(to) {
  const path = canonicalNavigationPath(to);
  if (path === '/mushroom-identify') return NAVIGATION_IDENTITIES['/mushrooms'];
  const route = Object.keys(NAVIGATION_IDENTITIES).find(key => path === key || path.startsWith(`${key}/`));
  return NAVIGATION_IDENTITIES[route] || NAVIGATION_IDENTITIES['/more'];
}

export function navigationStyle(to) {
  const identity = navigationIdentity(to);
  return { '--nav-color-light': identity.light, '--nav-color-dark': identity.dark,
    '--nav-color-active': identity.active };
}
